import { Field, ID, InputType, ObjectType } from 'type-graphql'
import { Prop } from '@typegoose/typegoose'
import GraphQLJSON from 'graphql-type-json'
import { TaskStatusEnum } from '../TaskStatusEnum'
import { generateEntityId } from '../../generateEntityId'

/**
 * Result returned after a task action hook execution.
 * Contains information about the execution outcome and any resulting data.
 */
@ObjectType()
@InputType('TaskActionResultInput')
export class TaskActionResult {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('tar') })
  _id: string

  /**
   * The ID of the action hook that was executed.
   */
  @Field(() => String)
  @Prop({ type: String, required: true })
  actionHookId: string

  /**
   * The task status that triggered this action hook execution.
   */
  @Field(() => TaskStatusEnum)
  @Prop({ type: String, required: true })
  status: TaskStatusEnum

  /**
   * Timestamp when the action hook was executed.
   */
  @Field(() => Date)
  @Prop({ type: Date, required: true })
  executedAt: Date

  /**
   * Whether the action hook executed successfully.
   */
  @Field(() => Boolean)
  @Prop({ type: Boolean, required: true })
  success: boolean

  /**
   * Error message if the execution failed.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  error?: string

  /**
   * Whether a rollback was executed due to failure.
   * Only applicable when the hook is critical and failed.
   */
  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  rollbackExecuted?: boolean

  /**
   * The parameters that were passed to the action hook.
   */
  @Field(() => GraphQLJSON, { nullable: true })
  @Prop({ type: Object })
  parameters?: Record<string, unknown>

  /**
   * Any data returned by the action hook execution.
   * The structure depends on the specific hook implementation.
   */
  @Field(() => GraphQLJSON, { nullable: true })
  @Prop({ type: Object })
  resultData?: Record<string, unknown>
}
